import React, { useCallback, useEffect, useRef, useState } from 'react';
import { inject, observer } from 'mobx-react';
import C from '../colors';
import { reportClientMonitorEvent } from '../clientMonitor';
import { isSameFolderName, matchLocalFiles, summarizeSourceFolders } from '../localFolderMatching';

function getOutputFolderName(dirname) {
  const safeName = String(dirname || '')
    .replace(/[\\/:*?"<>|]/g, '-')
    .replace(/\s+/g, ' ')
    .trim();
  return safeName ? `已选-${safeName}` : '已选';
}

const LocalSperaterModule = inject('snackbarStore')(observer(({
  snackbarStore,
  open,
  dirHandle,
  sourceFolderName,
  localPictures,
  pictures,
  onClose,
  onChangeFolder,
  dirname,
  orderRef,
  sourceSummary,
}) => {
  const [selected, setSelected] = useState([]);
  const [writing, setWriting] = useState(false);
  const [fileView, setFileView] = useState('selected');
  const outputFolderName = getOutputFolderName(dirname);
  const otherPictures = localPictures.filter(item => !selected.includes(item));
  const visiblePictures = fileView === 'selected' ? selected : otherPictures;
  const matchPercent = localPictures.length ? Math.round((selected.length / localPictures.length) * 100) : 0;
  const recommendedSource = sourceSummary.recommended;
  const sourceFolderMatches = recommendedSource
    ? isSameFolderName(sourceFolderName, recommendedSource.name)
    : null;

  const toggle = (name) => setSelected(current => (
    current.includes(name) ? current.filter(item => item !== name) : [...current, name]
  ));

  const writeToDir = useCallback(async () => {
    if (!selected.length || writing) return;
    setWriting(true);
    try {
      const newDir = await dirHandle.current.getDirectoryHandle(outputFolderName, { create: true });
      for (const name of selected) {
        const fileHandle = await dirHandle.current.getFileHandle(name);
        const newFileHandle = await newDir.getFileHandle(name, { create: true });
        const writable = await newFileHandle.createWritable();
        await writable.write(await (await fileHandle.getFile()).arrayBuffer());
        await writable.close();
      }
      reportClientMonitorEvent('app_folder_write_completed', {
        page: 'web/photographer/local-organizer',
        orderRef,
        requestedFileCount: selected.length,
        writtenFileCount: selected.length,
        immediate: true,
      });
      snackbarStore.showSnackbar(`已将 ${selected.length} 个文件整理到「${outputFolderName}」`, 'success');
      onClose();
    } catch (error) {
      reportClientMonitorEvent('app_folder_write_failed', {
        page: 'web/photographer/local-organizer',
        orderRef,
        level: 'error',
        requestedFileCount: selected.length,
        failureKind: String(error?.name || 'unknown').slice(0, 80),
        immediate: true,
      });
      snackbarStore.showSnackbar('整理文件失败，请确认文件夹仍有读写权限', 'warning');
    } finally {
      setWriting(false);
    }
  }, [selected, writing, dirHandle, outputFolderName, snackbarStore, onClose, orderRef]);

  useEffect(() => {
    if (!open) return;
    setFileView('selected');
    setSelected(matchLocalFiles(pictures, localPictures).selected);
  }, [pictures, open, localPictures]);

  if (!open) return null;
  return (
    <div onClick={onClose} style={{
      position: 'fixed', inset: 0, zIndex: 1200,
      background: 'rgba(26,20,23,.52)', backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 24,
    }}>
      <div onClick={event => event.stopPropagation()} style={{
        background: C.paper, borderRadius: 18, width: '100%', maxWidth: 540,
        boxShadow: '0 28px 70px -24px rgba(26,20,23,.45)', overflow: 'hidden',
      }}>
        <div style={{ padding: '24px 28px 18px', borderBottom: `1px solid ${C.hair}` }}>
          <div style={{ color: C.brand, fontSize: 10, fontWeight: 700, letterSpacing: 1.6, textTransform: 'uppercase', marginBottom: 8 }}>
            文件整理
          </div>
          <h3 style={{ margin: 0, fontFamily: C.serif, fontSize: 26, lineHeight: 1.15, fontWeight: 400 }}>
            整理已选文件
          </h3>
          <div style={{ marginTop: 7, fontSize: 12.5, color: C.ink3, lineHeight: 1.6 }}>
            从原片文件夹中匹配客户已选照片，并复制到新文件夹。不会移动或删除原文件。
          </div>
        </div>

        <div style={{ padding: '18px 20px 14px' }}>
          {recommendedSource && (
            <div data-testid="source-folder-comparison" style={{
              marginBottom: 12, padding: '10px 13px', borderRadius: 10,
              border: `1px solid ${sourceFolderMatches ? '#B9DFC8' : '#E8C89A'}`,
              background: sourceFolderMatches ? '#F1FAF5' : '#FFF8EA',
              color: sourceFolderMatches ? '#2F6F49' : '#8A5A16', fontSize: 11.5, lineHeight: 1.55,
            }}>
              {sourceFolderMatches
                ? `已选择上传来源文件夹「${recommendedSource.name}」`
                : `当前选择「${sourceFolderName}」，这批照片多数上传自「${recommendedSource.name}」；仍可继续按文件名匹配。`}
              {sourceSummary.unknownCount > 0 && ` 另有 ${sourceSummary.unknownCount} 张照片无法确定来源文件夹。`}
            </div>
          )}
          <div style={{ padding: 5, borderRadius: 15, background: 'rgba(247,119,176,.12)', boxShadow: 'inset 0 0 0 1px rgba(217,79,146,.12)' }}>
            <div data-testid="selected-output-path" style={{ padding: '15px 16px', borderRadius: 11, background: '#fff' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 14 }}>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, color: C.brand, fontWeight: 700, letterSpacing: 1.2, textTransform: 'uppercase' }}>
                    写入位置
                  </div>
                  <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 7, marginTop: 9, fontFamily: C.mono, fontSize: 13 }}>
                    <span data-testid="selected-source-folder" style={{ color: C.ink3 }}>
                      {sourceFolderName || '所选原片文件夹'}
                    </span>
                    <span style={{ color: C.hair2 }}>›</span>
                    <strong data-testid="selected-output-folder" style={{ color: C.brand2 }}>
                      {outputFolderName}
                    </strong>
                  </div>
                  <div style={{ marginTop: 7, color: C.ink3, fontSize: 11.5, lineHeight: 1.5 }}>
                    将在原片文件夹内新建「{outputFolderName}」并写入匹配文件。
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="change-selected-source-folder"
                  onClick={onChangeFolder}
                  style={{
                    flexShrink: 0, padding: '7px 10px', borderRadius: 9,
                    border: `1px solid ${C.hair2}`, background: C.paper, color: C.ink2,
                    fontFamily: C.sans, fontSize: 11.5, cursor: 'pointer',
                  }}
                >更换</button>
              </div>
            </div>
          </div>

          <div data-testid="selected-match-summary" style={{
            marginTop: 16, padding: '14px 16px', borderRadius: 12,
            background: C.ink, color: '#fff',
          }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-end', gap: 16 }}>
              <div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6 }}>
                  <strong style={{ fontFamily: C.serif, fontSize: 30, lineHeight: 1, fontWeight: 400, color: '#fff', fontVariantNumeric: 'tabular-nums' }}>
                    {selected.length}
                  </strong>
                  <span style={{ fontSize: 12.5, color: 'rgba(255,255,255,.78)' }}>个文件将写入</span>
                </div>
                <div style={{ marginTop: 5, fontSize: 11, color: 'rgba(255,255,255,.46)' }}>
                  已扫描 {localPictures.length} 个原片文件
                </div>
              </div>
              <div style={{ fontFamily: C.mono, fontSize: 11, color: C.brand }}>{matchPercent}%</div>
            </div>
            <div style={{ height: 3, marginTop: 12, borderRadius: 2, background: 'rgba(255,255,255,.12)', overflow: 'hidden' }}>
              <div style={{ width: `${matchPercent}%`, height: '100%', borderRadius: 2, background: C.brand, transition: 'width .45s cubic-bezier(.32,.72,0,1)' }} />
            </div>
          </div>

          <div style={{ display: 'flex', gap: 6, margin: '12px 0 8px' }}>
            {[
              { id: 'selected', label: '将写入', count: selected.length },
              { id: 'other', label: '其他文件', count: otherPictures.length },
            ].map(view => {
              const active = fileView === view.id;
              return (
                <button
                  key={view.id}
                  type="button"
                  data-testid={`show-${view.id}-files`}
                  onClick={() => setFileView(view.id)}
                  style={{
                    padding: '7px 11px', borderRadius: 9,
                    border: active ? 'none' : `1px solid ${C.hair2}`,
                    background: active ? C.brand : '#fff', color: active ? '#fff' : C.ink3,
                    fontFamily: C.sans, fontSize: 11.5, fontWeight: 600, cursor: 'pointer',
                    transition: 'transform .25s cubic-bezier(.32,.72,0,1), background .25s cubic-bezier(.32,.72,0,1)',
                  }}
                >{view.label} {view.count}</button>
              );
            })}
            <div style={{ marginLeft: 'auto', alignSelf: 'center', color: C.ink3, fontSize: 10.5 }}>
              点击文件可加入或排除
            </div>
          </div>

          <div data-testid="selected-file-results" style={{ height: 190, overflowY: 'auto', border: `1px solid ${C.hair}`, borderRadius: 10, background: '#fff' }}>
            {visiblePictures.length === 0 && (
              <div style={{ height: '100%', display: 'grid', placeItems: 'center', color: C.ink3, fontSize: 12 }}>
                {fileView === 'selected' ? '暂时没有要写入的文件' : '没有其他文件'}
              </div>
            )}
            {visiblePictures.map((item, index) => {
              const willWrite = selected.includes(item);
              return (
              <div key={item} onClick={() => toggle(item)} style={{
                display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10,
                padding: '8px 12px', cursor: 'pointer',
                background: willWrite ? 'rgba(247,119,176,.07)' : 'transparent',
                borderBottom: index === visiblePictures.length - 1 ? 'none' : `1px solid ${C.hair}`,
                transition: 'background .25s cubic-bezier(.32,.72,0,1)',
              }}>
                <div style={{ minWidth: 0, display: 'flex', alignItems: 'center', gap: 9 }}>
                  <span style={{
                    width: 22, height: 26, flexShrink: 0, borderRadius: 4,
                    display: 'grid', placeItems: 'center',
                    background: willWrite ? 'rgba(247,119,176,.16)' : C.paper2,
                    color: willWrite ? C.brand2 : C.ink3, fontFamily: C.mono, fontSize: 8, fontWeight: 700,
                  }}>IMG</span>
                  <span style={{ minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', fontFamily: C.mono, fontSize: 12, color: C.ink }}>
                    {item}
                  </span>
                </div>
                <span data-testid={`file-status-${willWrite ? 'write' : 'other'}`} style={{
                  flexShrink: 0, padding: '4px 7px', borderRadius: 8,
                  background: willWrite ? C.brand : C.paper2,
                  color: willWrite ? '#fff' : C.ink3,
                  fontSize: 9.5, fontWeight: 700,
                }}>{willWrite ? '将写入' : '可加入'}</span>
              </div>
              );
            })}
          </div>
        </div>

        <div style={{ padding: '14px 20px 20px', borderTop: `1px solid ${C.hair}`, display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '9px 16px', borderRadius: 9, border: `1px solid ${C.hair2}`, background: '#fff', color: C.ink2, fontFamily: C.sans, fontSize: 13, cursor: 'pointer' }}>
            关闭
          </button>
          <button
            type="button"
            data-testid="write-selected-files"
            disabled={!selected.length || writing}
            onClick={writeToDir}
            style={{
              padding: '10px 18px', borderRadius: 10, border: 'none',
              background: selected.length && !writing ? C.ink : C.hair2, color: '#fff',
              fontFamily: C.sans, fontSize: 13, fontWeight: 600,
              cursor: selected.length && !writing ? 'pointer' : 'not-allowed',
              boxShadow: selected.length && !writing ? '0 10px 24px -14px rgba(26,22,20,.7)' : 'none',
            }}
          >
            {writing ? '正在写入…' : `写入「${outputFolderName}」· ${selected.length} 个文件`}
          </button>
        </div>
      </div>
    </div>
  );
}));

const LocalSperater = inject('snackbarStore')(observer(({ snackbarStore, pictures, dirname, orderRef }) => {
  const dirHandle = useRef(null);
  const [open, setOpen] = useState(false);
  const [localPictures, setLocalPictures] = useState([]);
  const [sourceFolderName, setSourceFolderName] = useState('');
  const sourceSummary = summarizeSourceFolders(pictures || []);

  const openDir = async () => {
    if (!window.showDirectoryPicker) {
      snackbarStore.showSnackbar('当前浏览器不支持文件夹整理，请使用最新版 Chrome 或 Edge', 'warning');
      return;
    }
    try {
      dirHandle.current = await window.showDirectoryPicker();
      setSourceFolderName(dirHandle.current.name || '所选原片文件夹');
      const files = [];
      let ignoredDirectoryCount = 0;
      for await (const entry of dirHandle.current.values()) {
        if (entry.kind === 'file') files.push(entry.name);
        else if (entry.kind === 'directory') ignoredDirectoryCount += 1;
      }
      const matchResult = matchLocalFiles(pictures, files);
      reportClientMonitorEvent('app_folder_match_completed', {
        page: 'web/photographer/local-organizer',
        orderRef,
        expectedPictureCount: (pictures || []).length,
        scannedFileCount: files.length,
        matchedFileCount: matchResult.selected.length,
        unmatchedPictureCount: matchResult.unmatchedPictureCount,
        ignoredDirectoryCount,
        knownSourceFolderCount: sourceSummary.knownCount,
        sourceFolderVariantCount: sourceSummary.folders.length,
        sourceFolderNameMatched: sourceSummary.recommended
          ? isSameFolderName(dirHandle.current.name, sourceSummary.recommended.name)
          : null,
        immediate: true,
      });
      setLocalPictures(files);
      setOpen(true);
    } catch (error) {
      if (error.code !== 20 && error.name !== 'AbortError') {
        reportClientMonitorEvent('app_folder_open_failed', {
          page: 'web/photographer/local-organizer',
          orderRef,
          level: 'error',
          failureKind: String(error?.name || 'unknown').slice(0, 80),
          immediate: true,
        });
        snackbarStore.showSnackbar('打开本地文件夹失败', 'warning');
      }
    }
  };

  if (!pictures) return null;
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
      <button
        type="button"
        data-testid="organize-selected-files"
        disabled={!pictures.length}
        onClick={openDir}
        style={{
          display: 'inline-flex', alignItems: 'center', gap: 7,
          padding: '9px 14px', borderRadius: 9, border: 'none',
          background: pictures.length ? C.brand : C.hair2,
          color: pictures.length ? '#fff' : C.ink3,
          fontFamily: C.sans, fontSize: 13, fontWeight: 600,
          cursor: pictures.length ? 'pointer' : 'not-allowed',
          boxShadow: pictures.length ? '0 8px 20px -10px rgba(217,79,146,.8)' : 'none',
          whiteSpace: 'nowrap',
        }}
      >
        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
          <path d="M3 7.5h7l2 2h9v8.5a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
          <path d="M3 7.5V6a2 2 0 0 1 2-2h5l2 2h7a2 2 0 0 1 2 2v1.5"/>
          <path d="m9 14 2 2 4-4"/>
        </svg>
        整理到已选文件夹
      </button>
      <LocalSperaterModule
        open={open}
        dirHandle={dirHandle}
        sourceFolderName={sourceFolderName}
        localPictures={localPictures}
        pictures={pictures}
        dirname={dirname}
        orderRef={orderRef}
        sourceSummary={sourceSummary}
        onChangeFolder={openDir}
        onClose={() => setOpen(false)}
      />
    </div>
  );
}));

export default LocalSperater;
